/**
 * Equivalent monthly rate from an annual nominal rate.
 *
 * `rm = (1 + r)^(1/12) - 1`
 *
 * @see FORMULAS.md §1 (rm definition) and §4 (required monthly investment).
 */
export function monthlyRate(annual: number): number {
  return Math.pow(1 + annual, 1 / 12) - 1;
}

/**
 * Real annual return via the Fisher relation.
 *
 * `rreal = (1 + r) / (1 + i) - 1`
 *
 * @see FORMULAS.md §2.
 */
export function realReturn(nominal: number, inflation: number): number {
  return (1 + nominal) / (1 + inflation) - 1;
}

/**
 * First-year PMT for a growing-contribution schedule.
 *
 * Closed form for a geometric-gradient annuity. Falls back to the
 * rm === kg (or numerically equal) edge case where the gradient
 * degenerates into a flat schedule.
 */
export function growingPMT(
  fvNeeded: number,
  rm: number,
  kg: number,
  n: number,
): number {
  if (n === 0) return 0;
  if (Math.abs(rm - kg) < 1e-15) {
    return fvNeeded / (n * Math.pow(1 + rm, n - 1));
  }
  const denominator = Math.pow(1 + rm, n) - Math.pow(1 + kg, n);
  if (Math.abs(denominator) < 1e-15) {
    return fvNeeded / (n * Math.pow(1 + rm, n - 1));
  }
  return (fvNeeded * (rm - kg)) / denominator;
}

/**
 * Fixed PMT for a level-contribution schedule (annuity-due).
 *
 * Falls back to the rm === 0 edge case.
 */
export function fixedPMT(fvNeeded: number, rm: number, n: number): number {
  if (n === 0) return 0;
  if (Math.abs(rm) < 1e-15) {
    return fvNeeded / n;
  }
  const denominator = Math.pow(1 + rm, n) - 1;
  if (Math.abs(denominator) < 1e-15) {
    return fvNeeded / n;
  }
  return (fvNeeded * rm) / denominator;
}